import React, { memo } from 'react';
import { CheckCircle, Clock, FileText, ArrowRight, Eye, Sparkles, Home } from 'lucide-react';
type SessionCompleteScreenProps = {
  onBackToStories: () => void;
  onViewStory: (storyId: number) => void;
  storyId: number;
  duration: string;
  title: string;
};
export function SessionCompleteScreen({
  onBackToStories,
  onViewStory,
  storyId,
  duration,
  title
}: SessionCompleteScreenProps) {
  return <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 h-screen overflow-hidden" data-id="element-1651">
      <div className="max-w-5xl w-full" data-id="element-1652">
        <div className="grid lg:grid-cols-2 gap-8 items-center" data-id="element-1653">
          {/* Left Column - Success Message & Stats */}
          <div data-id="element-1654">
            {/* Success Icon with Animation */}
            <div className="flex justify-start mb-6" data-id="element-1655">
              <div className="relative" data-id="element-1656">
                <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl" data-id="element-1657">
                  <CheckCircle className="w-12 h-12 text-white" data-id="element-1658" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full animate-ping opacity-20" data-id="element-1659"></div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-5xl font-bold text-gray-900 mb-3" data-id="element-1660">
              Story Saved! 🎉
            </h1>
            <p className="text-gray-600 text-lg mb-8" data-id="element-1661">
              Your memory has been preserved successfully
            </p>

            {/* Stats Cards - Compact Horizontal */}
            <div className="space-y-3 mb-8" data-id="element-1662">
              {/* Story Title */}
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border-2 border-blue-100" data-id="element-1663">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1664">
                  <FileText className="w-5 h-5 text-white" data-id="element-1665" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1666">
                  <div className="text-xs font-medium text-blue-600 mb-0.5" data-id="element-1667">
                    Story Title
                  </div>
                  <div className="text-sm font-bold text-gray-900 truncate" data-id="element-1668">
                    {title}
                  </div>
                </div>
              </div>

              {/* Duration & Status - Side by Side */}
              <div className="grid grid-cols-2 gap-3" data-id="element-1669">
                <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border-2 border-purple-100" data-id="element-1670">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1671">
                    <Clock className="w-5 h-5 text-white" data-id="element-1672" />
                  </div>
                  <div className="flex-1" data-id="element-1673">
                    <div className="text-xs font-medium text-purple-600 mb-0.5" data-id="element-1674">
                      Duration
                    </div>
                    <div className="text-sm font-bold text-gray-900" data-id="element-1675">
                      {duration}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border-2 border-green-100" data-id="element-1676">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1677">
                    <CheckCircle className="w-5 h-5 text-white" data-id="element-1678" />
                  </div>
                  <div className="flex-1" data-id="element-1679">
                    <div className="text-xs font-medium text-green-600 mb-0.5" data-id="element-1680">
                      Status
                    </div>
                    <div className="text-sm font-bold text-gray-900" data-id="element-1681">Ready</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps - Compact */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 text-white shadow-lg" data-id="element-1682">
              <div className="flex items-start gap-3" data-id="element-1683">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1684">
                  <Sparkles className="w-4 h-4" data-id="element-1685" />
                </div>
                <div data-id="element-1686">
                  <h3 className="font-bold text-sm mb-1" data-id="element-1687">What's Next?</h3>
                  <p className="text-blue-50 text-xs leading-relaxed" data-id="element-1688">
                    Transform into a storybook, add tags, or generate an AI
                    summary.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Action Buttons */}
          <div className="space-y-4" data-id="element-1689">
            <button onClick={() => onViewStory(storyId)} className="w-full flex items-center justify-center gap-3 px-8 py-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-xl rounded-2xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-2xl hover:shadow-green-500/50" data-id="element-1690">
              <Eye className="w-6 h-6" data-id="element-1691" />
              View Your Story
              <ArrowRight className="w-6 h-6" data-id="element-1692" />
            </button>

            <button className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-xl" data-id="element-1693">
              <Sparkles className="w-5 h-5" data-id="element-1694" />
              Create Storybook
            </button>

            <button onClick={onBackToStories} className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-white text-gray-700 font-semibold text-lg rounded-xl hover:bg-gray-50 transition-all border-2 border-gray-200 shadow-lg" data-id="element-1695">
              <Home className="w-5 h-5" data-id="element-1696" />
              Back to Stories
            </button>
          </div>
        </div>
      </div>
    </div>;
}